import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { notificationsAPI } from '@/services/api';
import { 
  LayoutDashboard, 
  Wallet, 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  TrendingUp, 
  Bell, 
  User, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Gift,
  CheckCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/dashboard/deposits', icon: ArrowDownToLine, label: 'Deposits' },
  { path: '/dashboard/withdrawals', icon: ArrowUpFromLine, label: 'Withdrawals' },
  { path: '/dashboard/investments', icon: TrendingUp, label: 'Trading' },
  { path: '/dashboard/referrals', icon: Gift, label: 'Referrals' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  const profileButtonRef = useRef(null);
  const profileMenuRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const notificationMenuRef = useRef(null);

  // Close all on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setProfileMenuOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  // Body scroll lock for mobile menu only
  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [mobileMenuOpen]);

  // Global click handler for dropdowns - uses a single listener
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Handle profile menu
      if (profileMenuOpen) {
        const clickedProfile = profileButtonRef.current?.contains(e.target);
        const clickedProfileMenu = profileMenuRef.current?.contains(e.target);
        if (!clickedProfile && !clickedProfileMenu) {
          setProfileMenuOpen(false);
        }
      }
      
      // Handle notifications
      if (notificationsOpen) {
        const clickedBell = notificationButtonRef.current?.contains(e.target);
        const clickedNotifMenu = notificationMenuRef.current?.contains(e.target);
        if (!clickedBell && !clickedNotifMenu) {
          setNotificationsOpen(false);
        }
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setProfileMenuOpen(false);
        setNotificationsOpen(false);
      }
    };

    // Single global listener - more efficient
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [profileMenuOpen, notificationsOpen]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await notificationsAPI.getAll();
        const notifs = response.data.notifications || [];
        setNotifications(notifs.slice(0, 5));
        setUnreadCount(notifs.filter(n => !n.is_read).length);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Toggle functions - simple state toggles, no event prevention needed
  const toggleProfile = useCallback(() => {
    setNotificationsOpen(false);
    setProfileMenuOpen(prev => !prev);
  }, []);

  const toggleNotifications = useCallback(() => {
    setProfileMenuOpen(false);
    setNotificationsOpen(prev => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
    logout();
    navigate('/');
  }, [logout, navigate]);

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const NavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      end={item.path === '/dashboard'}
      onClick={onClick}
      className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
    >
      <item.icon className="w-5 h-5" />
      <span>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col z-30">
        <div className="flex flex-col flex-1 glass-card m-4 p-4">
          <div className="flex items-center gap-2 px-4 py-4 mb-6">
            <Wallet className="w-8 h-8 text-cyan-400" />
            <span className="text-2xl font-outfit font-bold gradient-text">Dexfolio</span>
          </div>
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>
          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="flex items-center gap-3 px-4 py-2">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-white/50 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 glass-card mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl">
          <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3">
            {/* Mobile Menu Button */}
            <button
              type="button"
              className="lg:hidden p-2.5 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>

            {/* Balance Display - Desktop */}
            <div className="hidden sm:flex items-center gap-6">
              <div className="text-right">
                <p className="text-xs text-white/50">Available Balance</p>
                <p className="text-lg font-mono font-bold text-emerald-400">
                  ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div className="text-right">
                <p className="text-xs text-white/50">Active Trading</p>
                <p className="text-lg font-mono font-bold text-cyan-400">
                  ${user?.invested_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
            </div>

            {/* Mobile Balance */}
            <div className="sm:hidden flex-1 text-center px-2">
              <p className="text-[10px] text-white/50">Balance</p>
              <p className="text-sm font-mono font-bold text-emerald-400">
                ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-1">
              {/* Notifications Button */}
              <div className="relative">
                <button
                  ref={notificationButtonRef}
                  type="button"
                  onClick={toggleNotifications}
                  className="p-2.5 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors relative min-w-[44px] min-h-[44px] flex items-center justify-center"
                  data-testid="notifications-btn"
                  aria-label="Notifications"
                  aria-expanded={notificationsOpen}
                >
                  <Bell className="w-5 h-5 text-white/70" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-hotpink-400 rounded-full text-[10px] flex items-center justify-center text-white font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Panel - Mobile Responsive */}
                {notificationsOpen && (
                  <div
                    ref={notificationMenuRef}
                    className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-[60px] sm:top-full sm:mt-2 
                               w-auto sm:w-80 md:w-96 max-w-[calc(100vw-1rem)] 
                               bg-[#0a0f1a] border border-white/10 rounded-xl shadow-2xl z-[100]"
                    style={{ 
                      animation: 'dropdownIn 0.15s ease-out',
                    }}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/10">
                      <h3 className="font-semibold text-white text-sm sm:text-base">Notifications</h3>
                      <div className="flex items-center gap-1 sm:gap-2">
                        {unreadCount > 0 && (
                          <button
                            type="button"
                            onClick={markAllAsRead}
                            className="text-[10px] sm:text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 min-h-[32px]"
                          >
                            <CheckCheck className="w-3 h-3" />
                            <span className="hidden xs:inline">Mark all read</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setNotificationsOpen(false)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                          aria-label="Close notifications"
                          data-testid="close-notifications-btn"
                        >
                          <X className="w-5 h-5 text-white/70 hover:text-white" />
                        </button>
                      </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-[50vh] sm:max-h-80 overflow-y-auto overscroll-contain">
                      {notifications.length > 0 ? (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`p-3 sm:p-4 border-b border-white/5 active:bg-white/10 transition-colors ${
                              !notif.is_read ? 'bg-cyan-500/5' : ''
                            }`}
                          >
                            <div className="flex items-start gap-2 sm:gap-3">
                              {!notif.is_read && (
                                <span className="w-2 h-2 rounded-full bg-cyan-400 mt-1.5 shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs sm:text-sm font-medium ${notif.is_read ? 'text-white/70' : 'text-white'}`}>
                                  {notif.title}
                                </p>
                                <p className="text-[10px] sm:text-xs text-white/50 mt-1 line-clamp-2">{notif.message}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-6 sm:p-8 text-center">
                          <Bell className="w-8 h-8 sm:w-10 sm:h-10 text-white/20 mx-auto mb-2 sm:mb-3" />
                          <p className="text-white/50 text-xs sm:text-sm">No new notifications</p>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="p-2 sm:p-3 border-t border-white/10">
                        <button
                          type="button"
                          onClick={() => {
                            setNotificationsOpen(false);
                            navigate('/dashboard/notifications');
                          }}
                          className="w-full py-2.5 text-xs sm:text-sm text-cyan-400 hover:bg-cyan-500/10 active:bg-cyan-500/20 rounded-lg transition-colors min-h-[44px]"
                        >
                          View all notifications
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Profile Button */}
              <div className="relative">
                <button
                  ref={profileButtonRef}
                  type="button"
                  onClick={toggleProfile}
                  className="flex items-center gap-1 sm:gap-2 p-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors min-h-[44px]"
                  data-testid="user-dropdown-btn"
                  aria-label="User menu"
                  aria-expanded={profileMenuOpen}
                >
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-cyan-400" />
                  </div>
                  <ChevronDown className={`w-4 h-4 text-white/50 transition-transform duration-200 hidden sm:block ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Profile Dropdown */}
                {profileMenuOpen && (
                  <div
                    ref={profileMenuRef}
                    className="absolute right-0 top-full mt-2 w-56 bg-[#0a0f1a] border border-white/10 rounded-xl shadow-2xl z-[100]"
                    style={{ animation: 'dropdownIn 0.15s ease-out' }}
                  >
                    {/* User Info */}
                    <div className="p-4 border-b border-white/10">
                      <p className="font-medium text-white truncate">{user?.name}</p>
                      <p className="text-xs text-white/50 truncate">{user?.email}</p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <button
                        type="button"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate('/dashboard/profile');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 active:bg-white/20 transition-colors min-h-[48px]"
                        data-testid="profile-menu-item"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate('/dashboard/notifications');
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/10 active:bg-white/20 transition-colors min-h-[48px]"
                        data-testid="notifications-menu-item"
                      >
                        <Bell className="w-4 h-4" />
                        Notifications
                        {unreadCount > 0 && (
                          <span className="ml-auto text-xs bg-hotpink-400 px-1.5 py-0.5 rounded">{unreadCount}</span>
                        )}
                      </button>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-white/10 py-2">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-hotpink-400 hover:bg-hotpink-400/10 active:bg-hotpink-400/20 transition-colors min-h-[48px]"
                        data-testid="logout-menu-item"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
              data-testid="mobile-menu-backdrop"
            />
            
            {/* Menu Panel */}
            <div
              className="fixed top-0 left-0 bottom-0 w-[280px] max-w-[85vw] glass-card z-50 lg:hidden p-4 overflow-y-auto"
              style={{ animation: 'slideIn 0.2s ease-out' }}
              data-testid="mobile-menu-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Wallet className="w-7 h-7 text-cyan-400" />
                  <span className="text-lg font-outfit font-bold gradient-text">Dexfolio</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2.5 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close menu"
                  data-testid="close-mobile-menu-btn"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Navigation */}
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <NavItem key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} />
                ))}
              </nav>

              {/* User Info */}
              <div className="border-t border-white/10 pt-4 mt-6">
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                    <p className="text-xs text-white/50 truncate">{user?.email}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 text-hotpink-400 hover:bg-hotpink-400/10 active:bg-hotpink-400/20 justify-start min-h-[48px]"
                  onClick={handleLogout}
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Page Content */}
        <main className="p-2 sm:p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes dropdownIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
